import { canAccessRoute } from '@supply/contracts'
import * as React from 'react'
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { AppShell } from '@/components/layout/AppShell'
import { LEGACY_REDIRECTS } from '@/components/layout/nav'
import { Loading } from '@/components/states'
import { Empty } from '@/components/states'
import { RealtimeProvider } from '@/lib/realtime'
import { SessionProvider, useSession } from '@/lib/session'
import { ErrorBoundary } from '@/modules/errors/ErrorBoundary'
import { ErrorReportHost } from '@/modules/errors/ErrorReportHost'
import { TaskDetailHost } from '@/modules/tasks/TaskDetailSheet'

// Só em dev: `?simular-erro=render|promise|chunk` (fora do build de produção).
const DevErrorTrigger = import.meta.env.DEV ? React.lazy(() => import('@/modules/errors/DevErrorTrigger')) : null

// Telas pesadas (gráficos, PDF) entram sob demanda.
const MyWeekPage = React.lazy(() => import('@/modules/me/MyWeekPage'))
const DashboardPage = React.lazy(() => import('@/modules/dashboard/DashboardPage'))
const BoardPage = React.lazy(() => import('@/modules/tasks/BoardPage'))
const CalendarPage = React.lazy(() => import('@/modules/calendar/CalendarPage'))
const TeamLoadPage = React.lazy(() => import('@/modules/load/TeamLoadPage'))
const RoutinesPage = React.lazy(() => import('@/modules/routines/RoutinesPage'))
const MeetingPage = React.lazy(() => import('@/modules/meeting/MeetingPage'))
const RasMeetingPage = React.lazy(() => import('@/modules/meeting/RasMeetingPage'))
const AtasPage = React.lazy(() => import('@/modules/meeting/AtasPage'))
const DecisionsPage = React.lazy(() => import('@/modules/decisions/DecisionsPage'))
const PerformancePage = React.lazy(() => import('@/modules/performance/PerformancePage'))
const ManagerDashboard = React.lazy(() => import('@/modules/manager/ManagerDashboard'))
const NotificationsPage = React.lazy(() => import('@/modules/notifications/NotificationsPage'))
const PeoplePage = React.lazy(() => import('@/modules/admin/PeoplePage'))
const SettingsPage = React.lazy(() => import('@/modules/admin/SettingsPage'))
const OrgChartPage = React.lazy(() => import('@/modules/org/OrgChartPage'))

/** Gestor entra no Dashboard; colaborador, na própria semana. */
function Home() {
  const { isGestor } = useSession()
  return <Navigate to={isGestor ? '/dashboard' : '/minha-semana'} replace />
}

/**
 * Executor não abre as telas gerenciais (Dashboard, Desempenho, Carga do time,
 * Configurações, Usuários): volta para Minha semana com aviso. O banco nega os dados de
 * qualquer jeito (ADR 0006); aqui é só para não abrir tela vazia.
 */
function RouteGuard({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation()
  const { me } = useSession()
  const allowed = canAccessRoute(me, pathname)
  React.useEffect(() => {
    if (!allowed) toast.warning('Tela restrita', { id: 'rota-restrita', description: 'Esta tela é só para gestor e gerentes.' })
  }, [allowed])
  if (!allowed) return <Navigate to="/minha-semana" replace />
  return <>{children}</>
}

/** Rota antiga → nova, mantendo `?tarefa=` e outros filtros da URL. */
function LegacyRedirect({ to }: { to: string }) {
  const { search, hash } = useLocation()
  return <Navigate to={`${to}${search}${hash}`} replace />
}

/** Erro numa tela derruba só o conteúdo: menu e topo seguem de pé. Trocar de rota limpa. */
function RouteErrorBoundary({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  return (
    <ErrorBoundary variant="area" resetKey={pathname} onHome={() => navigate('/')}>
      {children}
    </ErrorBoundary>
  )
}

export function App() {
  return (
    <BrowserRouter>
      <SessionProvider>
        <RealtimeProvider>
          <AppShell>
            <RouteErrorBoundary>
              <React.Suspense fallback={<Loading />}>
                {DevErrorTrigger && <DevErrorTrigger scope="area" />}
                <RouteGuard>
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/minha-semana" element={<MyWeekPage />} />
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/quadro" element={<BoardPage />} />
                    <Route path="/agenda" element={<CalendarPage />} />
                    <Route path="/carga" element={<TeamLoadPage />} />
                    <Route path="/rotinas" element={<RoutinesPage />} />
                    <Route path="/reuniao-ras" element={<RasMeetingPage />} />
                    <Route path="/reuniao" element={<MeetingPage />} />
                    <Route path="/atas" element={<AtasPage />} />
                    <Route path="/decisoes" element={<DecisionsPage />} />
                    <Route path="/desempenho" element={<PerformancePage />} />
                    <Route path="/gestor" element={<ManagerDashboard />} />
                    <Route path="/notificacoes" element={<NotificationsPage />} />
                    <Route path="/usuarios" element={<PeoplePage />} />
                    <Route path="/organograma" element={<OrgChartPage />} />
                    <Route path="/configuracoes/*" element={<SettingsPage />} />
                    {/* Links salvos das telas antigas de administração. */}
                    {LEGACY_REDIRECTS.map((r) => (
                      <Route key={r.from} path={r.from} element={<LegacyRedirect to={r.to} />} />
                    ))}
                    {/* Volta do logout do SSO: sessão já caiu; o gate leva ao login. */}
                    <Route path="/login" element={<Navigate to="/" replace />} />
                    <Route path="*" element={<Empty title="Página não encontrada" description="Use o menu para navegar." />} />
                  </Routes>
                </RouteGuard>
              </React.Suspense>
              <TaskDetailHost />
            </RouteErrorBoundary>
            <ErrorReportHost />
          </AppShell>
        </RealtimeProvider>
      </SessionProvider>
    </BrowserRouter>
  )
}
